/**
 * useContentEditor — single source of truth for the Website Builder content
 * editor's STATE + HANDLERS + side-effects.
 *
 * Extracted from DashboardContent.js in iter 128 (DRY) so that file becomes
 * orchestration + render only. Anything that mutates blocks, fetches data,
 * persists to the API, or wires the three canvas delegation hooks
 * (inline-edit / inline-button / drag-reorder) lives HERE.
 *
 * Return: a single `editor` object with every piece of state, setters, and
 * handlers the parent component needs. Spread it via `const editor = useContentEditor()`
 * and reference fields via `editor.X`.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { useInlineEditDelegation } from '../sqs/useInlineEditDelegation';
import { useScrollReveal } from '../sqs/useScrollReveal';
import { useCustomCssInjection } from '../sqs/useCustomCssInjection';
import { useHoverGlowInjection } from '../sqs/useHoverGlowInjection';
import { useFluidTextDrag } from '../sqs/useFluidTextDrag';
import { useFluidGrid } from '../sqs/useFluidGrid';
import { useMultiSelect } from '../sqs/useMultiSelect';
import '../sqs/scrollReveal.css';
import useInlineButtonEditor from '../sqs/useInlineButtonEditor';
import { useSectionSortable } from '../sqs/useSectionSortable';
import { FLUID_DEFAULT_CONTENT } from '../sqs/FluidCanvas';
import { applyLayoutTransform } from '../sqs/layoutEngine';
import { BLOCK_DEFAULTS } from './blockTypes';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function useContentEditor() {
  // ── Refs / DOM ──
  const canvasRef = useRef(null);

  // ── Block data ──
  const [blocks, setBlocks] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeBlock, setActiveBlock] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [editPanelBlock, setEditPanelBlock] = useState(null);

  // ── Media picker ──
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState(null);

  // ── Device view + view-mode global broadcaster ──
  const [viewMode, setViewMode] = useState('desktop');
  useEffect(() => {
    window.__wb_view_mode__ = viewMode;
    window.dispatchEvent(new CustomEvent('wb:view-mode-change', { detail: viewMode }));
  }, [viewMode]);

  // ── Pages ──
  const [pages, setPages] = useState([]);
  const [activePage, setActivePage] = useState(null);
  const [showNewPage, setShowNewPage] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [pageSaving, setPageSaving] = useState(false);
  const [editingPageTitle, setEditingPageTitle] = useState(null);

  // ── SQS panel state ──
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [addSectionAfter, setAddSectionAfter] = useState(-1);
  const [colorPanelBlock, setColorPanelBlock] = useState(null);
  const [layoutsPanelBlock, setLayoutsPanelBlock] = useState(null);
  const [siteTheme, setSiteTheme] = useState(null);
  const [siteMeta, setSiteMeta] = useState(null);
  const [addBlockTarget, setAddBlockTarget] = useState(null);
  const [imageEditorBlock, setImageEditorBlock] = useState(null);

  // ── AI ──
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiMode, setAiMode] = useState('section');
  const [aiInsertAfter, setAiInsertAfter] = useState(-1);

  // ── Fluid Engine — per-device editing (Iter 147 Session 3) ──
  const [device, setDevice] = useState('desktop'); // 'desktop' | 'tablet' | 'mobile'

  // ── Undo / Redo (Iter 173, expanded Iter 189b) ────────────────────
  // Snapshot-based history. Each entry is `{ blocks, label, ts }`.
  // ─ pushSnapshot debounces 350ms so rapid keystrokes coalesce into one step
  // ─ Stack capped at HISTORY_CAP; older entries roll off the bottom
  // ─ Persisted to sessionStorage per (site + page) so browser refresh keeps history
  // ─ Named actions: label inferred from the block diff (added / removed / edited / reordered)
  // ─ jumpTo(index) — for the History Panel — sets state to any snapshot in one step
  const HISTORY_CAP = 50;
  const historyRef = useRef({ stack: [], index: -1 });
  const isUndoingRef = useRef(false);
  const snapshotTimerRef = useRef(null);
  const pendingLabelRef = useRef(null); // action name queued by callers (optional)
  const [, setHistoryTick] = useState(0);
  const bumpHistoryUi = () => setHistoryTick((t) => (t + 1) % 1000000);

  // Session-storage key derived from the currently-open page. `activePage`
  // isn't declared yet at this point so we compute lazily below.
  const historyKey = useCallback(() => {
    // Runtime imports of activePage — accessed via closure below
    const pid = (typeof window !== 'undefined' && window.__wb_active_page_id) || 'home';
    return `wb:history:${pid}`;
  }, []);

  const persist = useCallback(() => {
    try {
      const key = historyKey();
      const payload = JSON.stringify(historyRef.current);
      if (payload.length < 1_500_000) { // ~1.5MB sessionStorage safety cap
        sessionStorage.setItem(key, payload);
      }
    } catch { /* quota exceeded — silently drop */ }
  }, [historyKey]);

  const inferLabel = (prev, next) => {
    // Called with previous and next `blocks` arrays. Returns a short verb.
    if (!Array.isArray(prev) || !Array.isArray(next)) return 'Edit';
    const p = prev.length, n = next.length;
    if (n > p) {
      // Added — find the new block's type
      const prevIds = new Set(prev.map((b) => b.id));
      const added = next.find((b) => !prevIds.has(b.id));
      return added ? `Add ${added.type || 'block'}` : 'Add block';
    }
    if (n < p) {
      const nextIds = new Set(next.map((b) => b.id));
      const removed = prev.find((b) => !nextIds.has(b.id));
      return removed ? `Remove ${removed.type || 'block'}` : 'Remove block';
    }
    // Same length — reorder or edit
    const prevOrder = prev.map((b) => b.id).join(',');
    const nextOrder = next.map((b) => b.id).join(',');
    if (prevOrder !== nextOrder) return 'Reorder blocks';
    // Same order — find the changed block
    for (let i = 0; i < prev.length; i++) {
      if (prev[i] !== next[i]) return `Edit ${next[i]?.type || 'block'}`;
    }
    return 'Edit';
  };

  const pushSnapshot = useCallback((next, forcedLabel) => {
    const { stack, index } = historyRef.current;
    const truncated = stack.slice(0, index + 1);
    const prevBlocks = truncated.length ? truncated[truncated.length - 1].blocks : [];
    const label = forcedLabel || pendingLabelRef.current || inferLabel(prevBlocks, next);
    pendingLabelRef.current = null;
    const entry = { blocks: next, label, ts: Date.now() };
    const newStack = [...truncated, entry];
    const final = newStack.length > HISTORY_CAP ? newStack.slice(-HISTORY_CAP) : newStack;
    historyRef.current = { stack: final, index: final.length - 1 };
    bumpHistoryUi();
    persist();
  }, [persist]);

  const undo = useCallback(() => {
    const { stack, index } = historyRef.current;
    if (index <= 0) return;
    const target = stack[index - 1];
    historyRef.current = { stack, index: index - 1 };
    isUndoingRef.current = true;
    setBlocks(target.blocks);
    setHasChanges(true);
    bumpHistoryUi();
    persist();
    const currentEntry = stack[index]; // the action we just undid
    toast.success(`Undone: ${currentEntry?.label || 'edit'}`, { duration: 1600 });
    setTimeout(() => { isUndoingRef.current = false; }, 50);
  }, [persist]);

  const redo = useCallback(() => {
    const { stack, index } = historyRef.current;
    if (index >= stack.length - 1) return;
    const target = stack[index + 1];
    historyRef.current = { stack, index: index + 1 };
    isUndoingRef.current = true;
    setBlocks(target.blocks);
    setHasChanges(true);
    bumpHistoryUi();
    persist();
    toast.success(`Redone: ${target?.label || 'edit'}`, { duration: 1600 });
    setTimeout(() => { isUndoingRef.current = false; }, 50);
  }, [persist]);

  const jumpTo = useCallback((targetIndex) => {
    const { stack, index } = historyRef.current;
    if (targetIndex < 0 || targetIndex >= stack.length || targetIndex === index) return;
    const target = stack[targetIndex];
    historyRef.current = { stack, index: targetIndex };
    isUndoingRef.current = true;
    setBlocks(target.blocks);
    setHasChanges(true);
    bumpHistoryUi();
    persist();
    toast.success(`Jumped to: ${target?.label || 'edit'}`, { duration: 1600 });
    setTimeout(() => { isUndoingRef.current = false; }, 50);
  }, [persist]);

  // Callers wanting to label their own action can call this before mutating.
  // Optional — the diff-inferer picks a good default if omitted.
  const markNextAction = useCallback((label) => {
    pendingLabelRef.current = label;
  }, []);

  const canUndo = historyRef.current.index > 0;
  const canRedo = historyRef.current.index < historyRef.current.stack.length - 1;
  const historyList = historyRef.current.stack; // for the panel
  const historyIndexNow = historyRef.current.index;

  // Snapshot on every meaningful blocks change. Debounced so a stream of
  // keystrokes in inline-text editors collapses into one undo entry.
  // Iter 173 — deferred seeding: don't start tracking history until the
  // initial /api/content fetch finishes AND blocks have actually loaded.
  // Otherwise the first snapshot would be the empty `[]` placeholder and
  // pressing Undo would wipe the canvas.
  useEffect(() => {
    if (isUndoingRef.current) return;
    if (!blocks) return;
    if (loading) return; // wait for first fetch to settle
    if (historyRef.current.stack.length === 0) {
      // Attempt restore from sessionStorage — only if last snapshot matches
      // the newly-loaded blocks (same page, no external mutation)
      try {
        const raw = sessionStorage.getItem(historyKey());
        if (raw) {
          const parsed = JSON.parse(raw);
          const tip = parsed?.stack?.[parsed.stack.length - 1];
          if (tip && JSON.stringify(tip.blocks) === JSON.stringify(blocks)) {
            historyRef.current = parsed;
            bumpHistoryUi();
            return;
          }
        }
      } catch { /* fallthrough — start fresh */ }
      historyRef.current = {
        stack: [{ blocks, label: 'Initial', ts: Date.now() }],
        index: 0,
      };
      bumpHistoryUi();
      persist();
      return;
    }
    const current = historyRef.current.stack[historyRef.current.index]?.blocks;
    if (current === blocks) return;
    clearTimeout(snapshotTimerRef.current);
    snapshotTimerRef.current = setTimeout(() => {
      pushSnapshot(blocks);
    }, 350);
    return () => clearTimeout(snapshotTimerRef.current);
  }, [blocks, loading, pushSnapshot, historyKey, persist]);

  // Global Cmd/Ctrl+Z (undo) + Cmd/Ctrl+Shift+Z (redo) shortcuts.
  // Skipped when the user is typing into an input/textarea/contentEditable
  // so the browser's native text-undo still works inside editor fields.
  useEffect(() => {
    const onKey = (e) => {
      const key = e.key?.toLowerCase();
      if (key !== 'z') return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const t = e.target;
      const isTyping = t && (
        t.tagName === 'INPUT' ||
        t.tagName === 'TEXTAREA' ||
        t.isContentEditable
      );
      if (isTyping) return;
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  // Legacy aliases for callers expecting useState pairs (kept for compat).
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const palette = JSON.parse(localStorage.getItem('wb_palette') || '["#8B5CF6","#3B82F6"]');

  // ── Initial fetch: content + products + pages + site meta ──
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/content`, { headers }),
      axios.get(`${API}/products`, { headers }),
      axios.get(`${API}/pages`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/builder/my-site`, { headers }).catch(() => ({ data: null })),
    ]).then(([contentRes, productsRes, pagesRes, siteRes]) => {
      setBlocks(contentRes.data?.blocks || []);
      setAllProducts(productsRes.data || []);
      setPages(pagesRes.data || []);
      window.__wb_pages = pagesRes.data || [];
      if (siteRes?.data) {
        setSiteMeta(siteRes.data);
        setSiteTheme(siteRes.data.theme || {});
        const t = siteRes.data.theme || {};
        const settings = siteRes.data.settings || {};
        if (Array.isArray(t.swatches) && t.swatches.length >= 5) window.__wb_site_palette__ = t.swatches;
        else if (Array.isArray(settings.palette_swatches) && settings.palette_swatches.length >= 5) window.__wb_site_palette__ = settings.palette_swatches;
        window.__wb_site_heading__ = t.heading_font || t.fonts || settings.font_family || 'Inter';
        window.__wb_site_radius__ = t.button_radius ?? 8;
      }
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Beforeunload protection ──
  useEffect(() => {
    const handler = (e) => { if (hasChanges) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasChanges]);

  // ── Save ──
  const handleSave = async () => {
    setSaving(true);
    try {
      if (activePage) {
        await axios.put(`${API}/pages/${activePage.id}`, { blocks }, { headers });
        toast.success(`"${activePage.title}" saved`);
      } else {
        const productsBlock = blocks.find(b => b.type === 'products');
        if (productsBlock?.content?.featured_product_ids?.length > 0) {
          const featuredOrder = {};
          productsBlock.content.featured_product_ids.forEach((id, idx) => { featuredOrder[id] = idx; });
          await axios.put(`${API}/products/bulk-featured`, { featured_product_ids: productsBlock.content.featured_product_ids, featured_order: featuredOrder }, { headers });
        }
        await axios.put(`${API}/content`, { blocks }, { headers });
        toast.success('Content saved');
        // Iter 197 — Notify PublishBar that draft state has changed
        try { window.dispatchEvent(new CustomEvent('wb:content-saved')); } catch {}
      }
      setHasChanges(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // ── Page navigation + CRUD ──
  const switchToPage = async (page) => {
    if (hasChanges && !(await themedConfirm('You have unsaved changes. Switch page anyway?'))) return;
    setEditPanelBlock(null);
    // Iter 173 — Reset undo history on page switch so undo doesn't bleed
    // a previous page's blocks into the active one.
    historyRef.current = { stack: [], index: -1 };
    bumpHistoryUi();
    // Iter 189b — update the sessionStorage key hint so restore lands on
    // the new page's history bucket. We DON'T write the empty history here
    // — the useEffect below will attempt a restore + fall through to init.
    try { window.__wb_active_page_id = page?.id || 'home'; } catch { /* noop */ }
    if (!page) {
      setActivePage(null); setLoading(true);
      try { const res = await axios.get(`${API}/content`, { headers }); setBlocks(res.data?.blocks || []); } catch { setBlocks([]); }
      finally { setLoading(false); }
    } else {
      setActivePage(page); setLoading(true);
      try { const res = await axios.get(`${API}/pages/${page.id}`, { headers }); setBlocks(res.data?.blocks || []); } catch { setBlocks([]); }
      finally { setLoading(false); }
    }
    setHasChanges(false); setActiveBlock(null);
  };

  const handleCreatePage = async () => {
    if (!newPageTitle.trim()) return toast.error('Page title required');
    setPageSaving(true);
    try {
      const res = await axios.post(`${API}/pages`, { title: newPageTitle.trim() }, { headers });
      setPages(prev => [...prev, res.data]); setShowNewPage(false); setNewPageTitle('');
      toast.success(`Page "${res.data.title}" created`); switchToPage(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setPageSaving(false); }
  };

  const handleDeletePage = async (page) => {
    if (!(await themedConfirm(`Delete "${page.title}"?`))) return;
    try {
      await axios.delete(`${API}/pages/${page.id}`, { headers });
      setPages(prev => prev.filter(p => p.id !== page.id));
      if (activePage?.id === page.id) switchToPage(null);
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  const handleRenamePageSave = async (page, newTitle) => {
    if (!newTitle.trim()) return;
    try {
      await axios.put(`${API}/pages/${page.id}`, { title: newTitle.trim() }, { headers });
      setPages(prev => prev.map(p => p.id === page.id ? { ...p, title: newTitle.trim() } : p));
      if (activePage?.id === page.id) setActivePage(prev => ({ ...prev, title: newTitle.trim() }));
      setEditingPageTitle(null);
    } catch { toast.error('Failed'); }
  };

  // ── Block mutations ──
  const updateBlock = useCallback((blockId, field, value) => {
    if (field === '_open_panel') { setEditPanelBlock(blockId); return; }
    // Iter 171 — support direct layout swap via `_layout` so block-type
    // editors (e.g. DonationsEditor) can switch variants without leaking
    // it into block.content.
    if (field === '_layout') {
      setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, layout: value } : b));
      setHasChanges(true);
      return;
    }
    // Iter 175 — Section Audit. Support dotted paths so premium ext blocks
    // (footer/contact/faq/product_grid/...) can wire inline-editable nested
    // fields like `info.phone`, `items.0.title`, `cols.2.links.3` without
    // building a custom updater per block.
    if (field && field.includes('.')) {
      const parts = field.split('.');
      setBlocks(prev => prev.map(b => {
        if (b.id !== blockId) return b;
        const root = { ...(b.content || {}) };
        let cursor = root;
        for (let i = 0; i < parts.length - 1; i++) {
          const k = parts[i];
          const nextKey = parts[i + 1];
          const isArrayKey = /^\d+$/.test(nextKey);
          // Clone container so React sees a fresh ref at every level.
          const existing = cursor[k];
          if (Array.isArray(existing)) {
            cursor[k] = existing.slice();
          } else if (existing && typeof existing === 'object') {
            cursor[k] = { ...existing };
          } else {
            cursor[k] = isArrayKey ? [] : {};
          }
          cursor = cursor[k];
        }
        const last = parts[parts.length - 1];
        cursor[last] = value;
        return { ...b, content: root };
      }));
      setHasChanges(true);
      return;
    }
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, content: { ...b.content, [field]: value } } : b));
    setHasChanges(true);
  }, []);

  const updateBlockContent = useCallback((blockId, newContent) => {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, content: newContent } : b));
    setHasChanges(true);
  }, []);

  const updateButtons = useCallback((blockId, nextButtons, field = 'buttons') => {
    setBlocks(prev => prev.map(b => {
      if (b.id !== blockId) return b;
      const c = b.content || {};
      // Support nested-path fields like "_style.buttons" so legacy About/Hero
      // blocks (which historically stored buttons under content._style.buttons)
      // remain editable via the same hook.
      if (field && field.includes('.')) {
        const parts = field.split('.');
        const head = parts[0];
        const tail = parts.slice(1);
        let nestedRoot = { ...(c[head] || {}) };
        let cursor = nestedRoot;
        for (let i = 0; i < tail.length - 1; i++) {
          const k = tail[i];
          cursor[k] = { ...(cursor[k] || {}) };
          cursor = cursor[k];
        }
        cursor[tail[tail.length - 1]] = nextButtons;
        return { ...b, content: { ...c, [head]: nestedRoot } };
      }
      return { ...b, content: { ...c, [field]: nextButtons } };
    }));
    setHasChanges(true);
  }, []);

  // ── Canvas delegation hooks (single set, single source of truth) ──
  useInlineEditDelegation({
    canvasRef, blocks, onUpdateBlockContent: updateBlockContent, enabled: true,
  });
  const inlineBtn = useInlineButtonEditor({
    canvasRef, blocks, onUpdateButtons: updateButtons, enabled: true,
  });
  useSectionSortable({
    canvasRef, blocks, onUpdateBlockContent: updateBlockContent, enabled: true,
  });
  useScrollReveal({ canvasRef, blocks, enabled: true });
  useCustomCssInjection({ pageId: 'home', enabled: true });
  useHoverGlowInjection(siteTheme);
  const fluidGrid = useFluidGrid({
    canvasRef, blocks, onUpdateBlockContent: updateBlockContent, enabled: true,
  });
  const multiSelect = useMultiSelect({ enabled: true });
  const fluidDrag = useFluidTextDrag({
    canvasRef, blocks, onUpdateBlockContent: updateBlockContent, enabled: true,
    fluidGrid, device, multiSelect,
  });

  const setBlockLayout = useCallback((blockId, newLayout) => {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, layout: newLayout } : b));
    setHasChanges(true);
    toast.success('Layout updated');
  }, []);

  const toggleVisibility = (blockId) => {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, is_visible: !b.is_visible } : b));
    setHasChanges(true);
  };

  const deleteBlock = async (blockId) => {
    if (!(await themedConfirm('Delete this block?'))) return;
    setBlocks(prev => {
      const f = prev.filter(b => b.id !== blockId);
      f.forEach((b, i) => (b.order = i));
      return f;
    });
    if (editPanelBlock === blockId) setEditPanelBlock(null);
    setHasChanges(true);
  };

  const moveBlock = (blockId, dir) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === blockId);
      if (idx < 0 || (dir === -1 && idx === 0) || (dir === 1 && idx === prev.length - 1)) return prev;
      const arr = [...prev];
      [arr[idx], arr[idx + dir]] = [arr[idx + dir], arr[idx]];
      arr.forEach((b, i) => (b.order = i));
      return arr;
    });
    setHasChanges(true);
  };

  const addBlockAt = (type, afterIndex = -1) => {
    const defaults = {
      hero: { video_url: '', image_url: '', focus_point: { x: 50, y: 50 }, title: 'Your Headline', tagline: 'Your tagline here', buttons: [{ text: 'Get Started', link: '#' }] },
      banner: { image_url: '', focus_point: { x: 50, y: 50 }, height: 'medium', text: 'Announcement goes here', buttons: [] },
      products: { title: 'Featured Products', subtitle: 'Browse our selection', featured_product_ids: [] },
      about: { image_url: '', title: 'About Us', text: 'Tell your story.', vision: '' },
      gallery: { title: 'Gallery', images: [], layout: 'grid' },
      donations: { title: 'Support Us', subtitle: 'Make a difference', amounts: [25, 50, 100, 250], accept_card: true, accept_paypal: true, paypal_email: '' },
      signup: { title: 'Stay Connected', subtitle: 'Join our newsletter' },
      custom_text: { title: '', text: 'Your content here...', alignment: 'center' },
      custom_image: { image_url: '', caption: '' },
      video: { video_url: '', title: '' },
      testimonial: { quote: '', author: '', role: '' },
      spacer: { height: '80px' },
      booking: { title: 'Book Now', subtitle: 'Choose a time', show_prices: true },
      form: { form_id: '', title: 'Contact', description: 'Get in touch', fields: [{ label: 'Name', type: 'text', required: true }, { label: 'Email', type: 'email', required: true }, { label: 'Message', type: 'textarea', required: false }] },
      embed: { url: '', title: '', embed_type: '', source: '', embeddable: false, embed_url: '', thumbnail: '', layout: 'wide', buttons: [] },
      single_image: { image_url: '', caption: '', shape: 'rectangle', alignment: 'center', linked_url: '', buttons: [] },
      marquee: { words: ['Indigenous Made', 'Authentic'], speed: 'medium', separator: 'dot', bg_color: '#0F172A', text_color: '#FFFFFF', font_size: 'lg' },
      fluid_canvas: { ...FLUID_DEFAULT_CONTENT, children: [...FLUID_DEFAULT_CONTENT.children.map(c => ({ ...c, id: `${c.type}-${Date.now()}-${Math.random().toString(36).slice(2,6)}` }))] },
    };
    const newBlock = { id: `${type}-${Date.now()}`, type, order: afterIndex + 1, is_visible: true, content: defaults[type] || BLOCK_DEFAULTS[type] || {} };
    setBlocks(prev => {
      const arr = [...prev];
      arr.splice(afterIndex + 1, 0, newBlock);
      arr.forEach((b, i) => (b.order = i));
      return arr;
    });
    setHasChanges(true);
  };

  const insertBlocksFromTemplate = (tpl, afterIndex = -1) => {
    if (!tpl?.blocks?.length) return;
    setBlocks(prev => {
      const arr = [...prev];
      const toInsert = tpl.blocks.map((b, i) => ({
        id: `${b.type}-${Date.now()}-${i}`,
        type: b.type,
        ...(b.layout ? { layout: b.layout } : {}),
        order: afterIndex + 1 + i,
        is_visible: true,
        content: { ...(b.content || {}) },
      }));
      arr.splice(afterIndex + 1, 0, ...toInsert);
      arr.forEach((b, i) => (b.order = i));
      return arr;
    });
    setHasChanges(true);
    toast.success(`Added "${tpl.label}"`);
  };

  const insertAiBlocks = (aiBlocks, afterIndex = -1) => {
    if (!aiBlocks?.length) return;
    setBlocks(prev => {
      const arr = [...prev];
      const toInsert = aiBlocks.map((b, i) => ({
        id: `${b.type}-ai-${Date.now()}-${i}`,
        type: b.type,
        order: afterIndex + 1 + i,
        is_visible: true,
        content: { ...(b.content || {}) },
      }));
      arr.splice(afterIndex + 1, 0, ...toInsert);
      arr.forEach((b, i) => (b.order = i));
      return arr;
    });
    setHasChanges(true);
  };

  const duplicateBlock = (blockId) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === blockId);
      if (idx < 0) return prev;
      const src = prev[idx];
      const clonedContent = JSON.parse(JSON.stringify(src.content || {}));
      // Reset fluid offsets on the clone so it doesn't overlap the original.
      if (clonedContent._fluid_offsets) delete clonedContent._fluid_offsets;
      const clone = { ...src, id: `${src.type}-${Date.now()}`, content: clonedContent };
      const arr = [...prev];
      arr.splice(idx + 1, 0, clone);
      arr.forEach((b, i) => (b.order = i));
      return arr;
    });
    setHasChanges(true);
    toast.success('Section duplicated');
  };

  // Listen for the floating-toolbar Duplicate button (fires
  // `wb:duplicate-block` with the block ID — see InlineEditing.js).
  useEffect(() => {
    const onDup = (e) => {
      const blockId = e?.detail?.blockId;
      if (blockId) duplicateBlock(blockId);
    };
    const onDupText = (e) => {
      const { blockId, text } = e?.detail || {};
      if (!blockId) return;
      setBlocks(prev => prev.map(b => {
        if (b.id !== blockId) return b;
        const c = b.content || {};
        const arr = Array.isArray(c._extras_text_blocks) ? c._extras_text_blocks : [];
        const newEntry = { id: `tx-${Date.now()}`, text: text || 'New text', kind: 'p' };
        return { ...b, content: { ...c, _extras_text_blocks: [...arr, newEntry] } };
      }));
      setHasChanges(true);
      toast.success('Text duplicated — drag to position');
    };
    // Iter 149 — "Save Layout" fires from the section toolbar bookmark icon.
    const onSaveLayout = async (e) => {
      const { blockId, label } = e?.detail || {};
      if (!blockId || !label) return;
      const block = blocks.find(b => b.id === blockId);
      if (!block) return;
      try {
        const r = await axios.post(`${API}/builder/saved_layouts`, {
          label,
          block: { type: block.type, content: block.content || {} },
        });
        if (r.data?.id) toast.success(`Saved "${label}" to your library`);
      } catch (err) {
        toast.error('Could not save layout');
      }
    };
    window.addEventListener('wb:duplicate-block', onDup);
    window.addEventListener('wb:duplicate-text', onDupText);
    window.addEventListener('wb:save-layout', onSaveLayout);
    return () => {
      window.removeEventListener('wb:duplicate-block', onDup);
      window.removeEventListener('wb:duplicate-text', onDupText);
      window.removeEventListener('wb:save-layout', onSaveLayout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks]);

  const favoriteBlock = (blockId) => {
    setBlocks(prev => prev.map(b => {
      if (b.id !== blockId) return b;
      const s = b.content?._style || {};
      return { ...b, content: { ...b.content, _style: { ...s, favorited: !s.favorited } } };
    }));
    setHasChanges(true);
  };

  const setSectionHeight = useCallback((blockId, value) => {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, content: { ...b.content, section_height: value } } : b));
    setHasChanges(true);
  }, []);

  const setSectionAttached = useCallback((blockId, value) => {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, content: { ...b.content, attached: !!value } } : b));
    setHasChanges(true);
  }, []);

  const applyLayout = (blockId, layoutIdOrVariant) => {
    if (layoutIdOrVariant && typeof layoutIdOrVariant === 'object' && Array.isArray(layoutIdOrVariant.blocks)) {
      const variant = layoutIdOrVariant;
      setBlocks(prev => {
        const idx = prev.findIndex(b => b.id === blockId);
        if (idx === -1) return prev;
        const existing = prev[idx];
        const newBlocks = (variant.blocks || []).map((vb, i) => ({
          ...vb,
          id: 'b_' + Date.now() + '_' + i,
          order: existing.order + (i * 0.001),
          content: {
            ...(vb.content || {}),
            ...(existing.content?.title && !vb.content?.title ? { title: existing.content.title } : {}),
            ...(existing.content?.text && !vb.content?.text ? { text: existing.content.text } : {}),
            ...(existing.content?.image_url && !vb.content?.image_url ? { image_url: existing.content.image_url } : {}),
          },
          is_visible: true,
        }));
        const next = [...prev];
        next.splice(idx, 1, ...newBlocks);
        return next;
      });
      setHasChanges(true);
      toast.success(`Layout: "${variant.label}"`);
      return;
    }
    setBlocks(prev => prev.map(b => {
      if (b.id !== blockId) return b;
      return { ...b, content: applyLayoutTransform(b, layoutIdOrVariant) };
    }));
    setHasChanges(true);
    toast.success('Layout updated');
  };

  const openMediaPicker = (blockId, field) => {
    setMediaPickerTarget({ blockId, field });
    setShowMediaPicker(true);
  };

  const handleMediaSelect = (url) => {
    if (!mediaPickerTarget) return;
    const { blockId, field } = mediaPickerTarget;
    if (field === 'images') {
      setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, content: { ...b.content, images: [...(b.content.images || []), url] } } : b));
    } else {
      updateBlock(blockId, field, url);
    }
    setHasChanges(true);
  };

  return {
    // refs
    canvasRef,
    // iter 156 — exposes startResize for the portal-rendered handle
    fluidGrid,
    // block data + state
    blocks, setBlocks, allProducts, loading, saving,
    activeBlock, setActiveBlock,
    hasChanges, setHasChanges,
    editPanelBlock, setEditPanelBlock,
    showMediaPicker, setShowMediaPicker,
    mediaPickerTarget, setMediaPickerTarget,
    viewMode, setViewMode,
    // pages
    pages, setPages,
    activePage, setActivePage,
    showNewPage, setShowNewPage,
    newPageTitle, setNewPageTitle,
    pageSaving,
    editingPageTitle, setEditingPageTitle,
    // SQS panels
    addSectionOpen, setAddSectionOpen,
    addSectionAfter, setAddSectionAfter,
    colorPanelBlock, setColorPanelBlock,
    layoutsPanelBlock, setLayoutsPanelBlock,
    siteTheme, setSiteTheme, siteMeta,
    addBlockTarget, setAddBlockTarget,
    imageEditorBlock, setImageEditorBlock,
    // AI
    aiModalOpen, setAiModalOpen,
    aiMode, setAiMode,
    aiInsertAfter, setAiInsertAfter,
    // misc
    history, historyIndex, setHistory, setHistoryIndex,
    // Iter 173 — Undo / Redo wired with debounced 50-step snapshot history.
    // Iter 189b — expanded: jumpTo(index), historyList for a panel, markNextAction(label) for custom labels.
    undo, redo, canUndo, canRedo,
    jumpTo, historyList, historyIndexNow, markNextAction,
    palette, headers,
    inlineBtn,
    fluidDrag,
    multiSelect,
    device, setDevice,
    // handlers
    handleSave,
    switchToPage, handleCreatePage, handleDeletePage, handleRenamePageSave,
    updateBlock, updateBlockContent, updateButtons,
    setBlockLayout, toggleVisibility, deleteBlock, moveBlock,
    addBlockAt, insertBlocksFromTemplate, insertAiBlocks,
    duplicateBlock, favoriteBlock,
    setSectionHeight, setSectionAttached,
    applyLayout,
    openMediaPicker, handleMediaSelect,
  };
}
